import { useState, useEffect, useMemo } from "react";
import { View, Text, Dialog } from "./base";
import { Icon } from "./base/Icon";
import { Spinner } from "./base/Spinner";
import { useAppDispatch, useAppSelector } from "../redux";
import { getSessionTrace, TraceSpan } from "../redux/reducers/sessionTraceSlice";
import { SpanDetailView } from "./traces/SpanDetailView";
import { safeString } from "../utils/strings";

interface SessionTraceViewerProps {
  agentId?: string;
  sessionId: string;
}

const formatDuration = (ms: number): string => {
  if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3 });
};

const getStatusColor = (statusCode: string): "default" | "success" | "error" => {
  switch (statusCode?.toUpperCase()) {
    case "OK":
      return "success";
    case "ERROR":
      return "error";
    default:
      return "default";
  }
};

// Helper to get span kind info
const getSpanKindInfo = (span: TraceSpan) => {
  const spanKindValue = span.spanKind || (span.attributes as Record<string, unknown>)["openinference.span.kind"];
  const spanKind = typeof spanKindValue === "string" ? spanKindValue : undefined;
  const isLLMSpan = spanKind === "LLM";
  const isToolSpan = spanKind === "TOOL";
  const isAgentSpan = spanKind?.toLowerCase() === "agent";
  return { spanKind, isLLMSpan, isToolSpan, isAgentSpan };
};

// Map eventName to icon
const getEventIcon = (eventName: string | null | undefined): string => {
  switch (eventName) {
    case "start":
      return "play";
    case "stop":
      return "stop";
    case "text":
      return "message";
    case "text_to_speech":
      return "volume-up";
    case "audio":
      return "music";
    case "timeout":
      return "clock";
    case "log":
      return "snippet";
    case "start_session":
      return "rocket";
    case "session_end":
      return "logout";
    case "error":
      return "error";
    case "silence":
      return "mute";
    case "interrupt":
    case "turn_interrupted":
      return "close";
    case "initialize":
      return "play";
    case "handler_end":
    case "turn_end":
      return "check";
    case "debug":
      return "code";
    case "testing":
      return "testing";
    case "context":
      return "info";
    case "stt_model_switched":
    case "stt_update_settings":
      return "settings";
    case "start_recording":
      return "mic";
    case "stop_recording":
      return "mute";
    case "connect_sts":
    case "update_call":
    case "transfer_session":
      return "phone";
    case "disconnect_sts":
      return "close";
    case "collect_payment":
    case "collect_payment_success":
      return "link";
    case "supervisor_request":
    case "supervisor_response":
      return "user";
    case "merge_session":
      return "link";
    case "transcript_part":
      return "snippet";
    case "input_allowed":
    case "dtmf":
      return "keyboard";
    case "external_event":
    case "custom_event":
      return "zap";
    default:
      return "link";
  }
};

// Get icon for a span
const getSpanIcon = (span: TraceSpan): string => {
  const { isLLMSpan, isToolSpan, isAgentSpan } = getSpanKindInfo(span);
  return isLLMSpan ? "sparkles" : isToolSpan ? "tool" : isAgentSpan ? "bot" : getEventIcon(span.eventName);
};

// Get icon color for a span
const getSpanIconColor = (span: TraceSpan): string => {
  const { isLLMSpan, isToolSpan, isAgentSpan } = getSpanKindInfo(span);
  if (isLLMSpan) return "text-indigo-500 dark:text-indigo-400";
  if (isToolSpan) return "text-amber-500 dark:text-amber-400";
  if (isAgentSpan) return "text-violet-500 dark:text-violet-400";
  switch (span.eventName) {
    case "stop":
    case "session_end":
      return "text-red-500 dark:text-red-400";
    case "start_session":
      return "text-orange-500 dark:text-orange-400";
    case "start":
      return "text-green-500 dark:text-green-400";
    case "text_to_speech":
    case "audio":
      return "text-blue-500 dark:text-blue-400";
    case "text":
      return "text-emerald-400 dark:text-emerald-300";
    case "timeout":
      return "text-orange-400 dark:text-orange-300";
    default:
      return "text-gray-400 dark:text-gray-500";
  }
};

// Get display name for a span
const getSpanDisplayName = (span: TraceSpan): string => {
  const { isAgentSpan } = getSpanKindInfo(span);
  return isAgentSpan ? "Agent Session" : span.name;
};

// Get tooltip for a span icon
const getSpanIconTooltip = (span: TraceSpan): string => {
  const { spanKind, isLLMSpan, isToolSpan, isAgentSpan } = getSpanKindInfo(span);
  return isLLMSpan ? "LLM" : isToolSpan ? "TOOL" : isAgentSpan ? "agent" : span.eventName || spanKind || "unknown";
};

const SpanRow = ({
  span,
  depth = 0,
  isExpanded,
  onToggle,
  onSpanClick,
}: {
  span: TraceSpan & { children?: TraceSpan[] };
  depth?: number;
  isExpanded: boolean;
  onToggle: () => void;
  onSpanClick: (span: TraceSpan) => void;
}) => {
  const hasChildren = span.children && span.children.length > 0;
  const paddingLeft = depth * 20 + 12;

  // Get preview text for certain event types
  const getPreviewText = (): string | null => {
    const attrs = span.attributes as Record<string, unknown>;
    try {
      switch (span.eventName) {
        case "text": {
          // Text events have input.value with event.data.text
          const inputValue = attrs["input.value"];
          if (typeof inputValue === "string") {
            const parsed = JSON.parse(inputValue);
            return safeString(parsed?.event?.data?.text);
          }
          return null;
        }
        case "text_to_speech": {
          // TTS events have output.value with data.text
          const outputValue = attrs["output.value"];
          if (typeof outputValue === "string") {
            const parsed = JSON.parse(outputValue);
            return safeString(parsed?.data?.text);
          }
          return null;
        }
        case "log": {
          // Log events have output.value with data.message
          const outputValue = attrs["output.value"];
          if (typeof outputValue === "string") {
            const parsed = JSON.parse(outputValue);
            return safeString(parsed?.data?.message);
          }
          return null;
        }
        default:
          return null;
      }
    } catch {
      return null;
    }
  };
  const previewText = getPreviewText();

  // Use helper functions for icon, color, display name
  const eventIcon = getSpanIcon(span);
  const eventTooltip = getSpanIconTooltip(span);
  const displayName = getSpanDisplayName(span);
  const iconColor = getSpanIconColor(span);

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const handleRowClick = () => {
    onSpanClick(span);
  };

  return (
    <View
      className="border-b border-gray-100 dark:border-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors cursor-pointer"
      onClick={handleRowClick}
    >
      <View
        className="flex flex-row items-center py-2 px-3 gap-3"
        style={{ paddingLeft }}
      >
        {hasChildren ? (
          <button
            onClick={handleToggleClick}
            className="p-0.5 -m-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex-shrink-0"
          >
            <Icon
              name={isExpanded ? "chevron-down" : "chevron-right"}
              size={14}
              className="text-gray-400"
            />
          </button>
        ) : (
          <View className="w-[14px] flex-shrink-0" />
        )}

        <View className="flex-1 min-w-0 flex flex-row items-center gap-3">
          <View className="flex flex-row items-center gap-2 flex-shrink-0">
            <Icon
              name={eventIcon}
              size={14}
              title={eventTooltip}
              className={iconColor}
            />
            <Text className="text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{displayName}</Text>
          </View>
          {previewText && <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{previewText}</Text>}
        </View>

        <View className="flex flex-row items-center gap-4 flex-shrink-0">
          <Text className="text-xs text-gray-500 dark:text-gray-400 font-mono whitespace-nowrap">{formatTimestamp(span.startTime)}</Text>
          <Text className="text-xs text-gray-700 dark:text-gray-300 font-mono w-20 text-right">{formatDuration(span.durationMs)}</Text>
        </View>
      </View>

      {span.statusMessage && (
        <View
          className="px-3 pb-2"
          style={{ paddingLeft: paddingLeft + 14 }}
        >
          <Text className="text-xs text-red-600 dark:text-red-400">{safeString(span.statusMessage)}</Text>
        </View>
      )}
    </View>
  );
};

export const SessionTraceViewer = ({ agentId, sessionId }: SessionTraceViewerProps) => {
  const dispatch = useAppDispatch();
  const { currentTrace, isLoading, error } = useAppSelector((state) => state.sessionTrace);
  const [expandedSpans, setExpandedSpans] = useState<Set<string>>(new Set());
  const [selectedSpan, setSelectedSpan] = useState<TraceSpan | null>(null);

  useEffect(() => {
    if (agentId && sessionId) {
      dispatch(getSessionTrace({ agentId, sessionId }));
    }
  }, [dispatch, agentId, sessionId]);

  // Build span tree from flat list
  const spanTree = useMemo(() => {
    const spans = currentTrace?.spans || [];
    if (spans.length === 0) return [];

    const spanMap = new Map<string, TraceSpan & { children: TraceSpan[] }>();
    const roots: (TraceSpan & { children: TraceSpan[] })[] = [];

    // First pass: create map with children arrays
    spans.forEach((span) => {
      spanMap.set(span.spanId, { ...span, children: [] });
    });

    // Second pass: build tree structure
    spans.forEach((span) => {
      const spanWithChildren = spanMap.get(span.spanId)!;
      if (span.parentId && spanMap.has(span.parentId)) {
        spanMap.get(span.parentId)!.children.push(spanWithChildren);
      } else {
        roots.push(spanWithChildren);
      }
    });

    // Sort by start time
    const sortSpans = (spans: (TraceSpan & { children: TraceSpan[] })[]) => {
      spans.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
      spans.forEach((span) => sortSpans(span.children as (TraceSpan & { children: TraceSpan[] })[]));
    };
    sortSpans(roots);

    return roots;
  }, [currentTrace]);

  // Expand all by default when trace data loads
  useEffect(() => {
    if (spanTree.length > 0) {
      const allSpanIds = new Set<string>();
      const collectIds = (spans: (TraceSpan & { children?: TraceSpan[] })[]) => {
        spans.forEach((span) => {
          allSpanIds.add(span.spanId);
          if (span.children) {
            collectIds(span.children as (TraceSpan & { children?: TraceSpan[] })[]);
          }
        });
      };
      collectIds(spanTree);
      setExpandedSpans(allSpanIds);
    }
  }, [spanTree]);

  const toggleSpan = (spanId: string) => {
    setExpandedSpans((prev) => {
      const next = new Set(prev);
      if (next.has(spanId)) {
        next.delete(spanId);
      } else {
        next.add(spanId);
      }
      return next;
    });
  };

  const renderSpan = (span: TraceSpan & { children?: TraceSpan[] }, depth = 0): React.ReactNode => {
    const isExpanded = expandedSpans.has(span.spanId);

    return (
      <View key={span.spanId}>
        <SpanRow
          span={span}
          depth={depth}
          isExpanded={isExpanded}
          onToggle={() => toggleSpan(span.spanId)}
          onSpanClick={setSelectedSpan}
        />
        {isExpanded && span.children && span.children.map((child) => renderSpan(child as TraceSpan & { children?: TraceSpan[] }, depth + 1))}
      </View>
    );
  };

  if (isLoading) {
    return (
      <View className="flex items-center justify-center py-8">
        <Spinner size={24} />
        <Text className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading trace data...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View className="flex flex-col items-center justify-center py-8">
        <Icon
          name="error"
          size={24}
          className="text-red-500 mb-2"
        />
        <Text className="text-sm text-red-600 dark:text-red-400">Failed to load trace data: {error}</Text>
      </View>
    );
  }

  const spans = currentTrace?.spans || [];
  const traceSummary = currentTrace?.trace;

  if (spans.length === 0) {
    return (
      <View className="flex flex-col items-center justify-center py-8">
        <Icon
          name="activity"
          size={32}
          className="text-gray-300 dark:text-gray-600 mb-2"
        />
        <Text className="text-sm text-gray-500 dark:text-gray-400">No trace data available for this session.</Text>
        <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1">Traces may take a moment to appear after a session ends.</Text>
      </View>
    );
  }

  // Calculate summary stats
  const totalDuration =
    traceSummary?.durationMs ??
    (spans.length > 0 ? Math.max(...spans.map((s) => new Date(s.endTime).getTime())) - Math.min(...spans.map((s) => new Date(s.startTime).getTime())) : 0);
  const errorCount = spans.filter((s) => s.statusCode?.toUpperCase() === "ERROR").length;

  return (
    <View className="flex flex-col h-full">
      {/* Header with stats */}
      <View className="flex flex-row items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-600">
        <View className="flex flex-row items-center gap-4">
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">{spans.length}</span> spans
          </Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">{formatDuration(totalDuration)}</span> total
          </Text>
          {errorCount > 0 && (
            <Text className="text-xs text-red-600 dark:text-red-400">
              <span className="font-medium">{errorCount}</span> error{errorCount !== 1 ? "s" : ""}
            </Text>
          )}
        </View>
        <View className="flex flex-row items-center gap-2">
          <button
            onClick={() => {
              const allSpanIds = new Set<string>();
              const collectIds = (spans: (TraceSpan & { children?: TraceSpan[] })[]) => {
                spans.forEach((span) => {
                  allSpanIds.add(span.spanId);
                  if (span.children) {
                    collectIds(span.children as (TraceSpan & { children?: TraceSpan[] })[]);
                  }
                });
              };
              collectIds(spanTree);
              setExpandedSpans(allSpanIds);
            }}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Expand all
          </button>
          <Text className="text-gray-300 dark:text-gray-600">|</Text>
          <button
            onClick={() => setExpandedSpans(new Set())}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Collapse all
          </button>
        </View>
      </View>

      {/* Span list */}
      <View className="flex-1 overflow-y-auto">{spanTree.map((span) => renderSpan(span))}</View>

      {/* Span Detail Modal */}
      <Dialog
        isOpen={selectedSpan !== null}
        onClose={() => setSelectedSpan(null)}
        title={
          selectedSpan && (
            <View className="flex flex-row items-center gap-3">
              <Icon
                name={getSpanIcon(selectedSpan)}
                size={20}
                className={getSpanIconColor(selectedSpan)}
              />
              <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">{getSpanDisplayName(selectedSpan)}</Text>
            </View>
          )
        }
        className="min-w-[85vw] w-[85vw] min-h-[85vh] h-[85vh]"
        size="flex"
      >
        {selectedSpan && <SpanDetailView span={selectedSpan} />}
      </Dialog>
    </View>
  );
};
